import React, { useState, useEffect } from 'react';
import {
  fetchAppointment,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  cancelAppointment,
  createCustomer,
  fetchUsers,
} from '../api';
import NewWorkOrderModal from '../newworkordermodal';
import useDuplicateCustomerCheck from '../customers/useDuplicateCustomerCheck';
import customerLabel from '../customers/customerLabel';
import AppointmentEmails from './appointmentemails';
import './appointmentmodal.css';

const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toLocalTimeStr = (date) => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

const CONFLICT_TITLES = {
  capacity_exceeded: 'Slot fully booked',
  outside_hours: 'Outside service hours',
  in_past: 'Date/time already passed',
  lead_time_required: 'Not enough notice',
  outside_booking_window: 'Too far in advance',
};
const CONFLICT_CODES = Object.keys(CONFLICT_TITLES);

const formatWhen = (date) => date.toLocaleString([], {
  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

// Staff edits never email the customer on their own. For the three that matter to
// them — a new time, a cancellation, or reviving a cancelled appointment — staff are
// asked first. Everything else (service, wheels, vehicle, notes, price…) stays internal.
const EMAIL_PROMPTS = {
  time: {
    title: 'Email the customer the new time?',
    yes: 'Save & email customer',
    no: "Save, don't email",
  },
  cancel: {
    title: 'Cancel this appointment?',
    yes: 'Cancel & email customer',
    no: "Cancel, don't email",
  },
  revive: {
    title: 'Email the customer a new confirmation?',
    yes: 'Save & email customer',
    no: "Save, don't email",
  },
};

const computeSuggestedPrice = (appointmentType, numberOfWheels) => {
  if (!appointmentType) return '';
  const wheels = Number(numberOfWheels) || 1;
  const base = Number(appointmentType.starting_price) || 0;
  const total = appointmentType.price_unit === 'per_wheel' ? base * wheels : base;
  return total.toFixed(2);
};

const computeSuggestedDatePromised = (dateStr, appointmentType) => {
  if (!dateStr || !appointmentType) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + (appointmentType.turnaround_days || 0));
  return toLocalDateStr(d);
};

// initialValues prefills a new appointment (e.g. from a sales inbox inquiry:
// { customer: {id, name}, customer_name, customer_phone, customer_email, vehicle,
// number_of_wheels, description }); onCreated gets the appointment once it's saved.
const AppointmentModal = ({ appointment, prefilledStart, appointmentTypes, slotIntervalMinutes, onClose, initialValues = null, onCreated = null }) => {
  const isEditMode = Boolean(appointment);
  const { confirmNewCustomer, duplicatePrompt } = useDuplicateCustomerCheck();

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [bookingConflict, setBookingConflict] = useState(null);
  // { kind: 'time' | 'cancel' | 'revive', payload, fromCancelButton, oldStart, newStart }
  const [emailPrompt, setEmailPrompt] = useState(null);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);

  const [customerName, setCustomerName] = useState(initialValues?.customer?.name || initialValues?.customer_name || '');
  const [selectedCustomer, setSelectedCustomer] = useState(initialValues?.customer || null);
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Once true, the price/date-promised auto-suggestion effects stop overwriting the
  // field — set immediately on loading an existing appointment (never silently rewrite
  // already-stored values) and the moment the user edits either field directly.
  const [priceTouched, setPriceTouched] = useState(false);
  const [datePromisedTouched, setDatePromisedTouched] = useState(false);
  // The loaded appointment's own length and service, so re-saving it after its service's
  // slot interval changes keeps the length it was booked with instead of resizing it.
  const [original, setOriginal] = useState(null);
  // The customer asked not to be emailed — no "email the customer?" prompts.
  const [emailOptedOut, setEmailOptedOut] = useState(false);

  const [formData, setFormData] = useState({
    customer_phone: '',
    customer_email: '',
    appointment_type: appointmentTypes && appointmentTypes.length > 0 ? appointmentTypes[0].id : '',
    vehicle: '',
    date: prefilledStart ? toLocalDateStr(prefilledStart) : toLocalDateStr(new Date()),
    time: prefilledStart ? toLocalTimeStr(prefilledStart) : '09:00',
    status: 'scheduled',
    number_of_wheels: 1,
    price: '',
    date_promised: '',
    description: '',
    staff_notes: '',
    customer_notes: '',
    source: 'internal',
    marketing_channel: '',
    marketing_source: '',
    ...(initialValues ? {
      customer_phone: initialValues.customer_phone || '',
      customer_email: initialValues.customer_email || '',
      vehicle: initialValues.vehicle || '',
      number_of_wheels: initialValues.number_of_wheels || 1,
      description: initialValues.description || '',
    } : {}),
  });

  useEffect(() => {
    if (!isEditMode) return;
    const loadDetail = async () => {
      try {
        const full = await fetchAppointment(appointment.id);
        const start = new Date(full.start_datetime);
        setOriginal({
          appointmentType: String(full.appointment_type),
          durationMs: new Date(full.end_datetime).getTime() - start.getTime(),
          startMs: start.getTime(),
          status: full.status,
        });
        setEmailOptedOut(Boolean(full.customer_email_opted_out));
        setCustomerName(full.customer_name || '');
        if (full.customer) {
          setSelectedCustomer({ id: full.customer, name: full.customer_name });
        }
        setFormData({
          customer_phone: full.customer_phone || '',
          customer_email: full.customer_email || '',
          appointment_type: full.appointment_type || '',
          vehicle: full.vehicle || '',
          date: toLocalDateStr(start),
          time: toLocalTimeStr(start),
          status: full.status,
          number_of_wheels: full.number_of_wheels || 1,
          price: full.price != null ? String(full.price) : '',
          date_promised: full.date_promised || '',
          description: full.description || '',
          staff_notes: full.staff_notes || '',
          customer_notes: full.customer_notes || '',
          source: full.source,
          marketing_channel: full.marketing_channel || '',
          marketing_source: full.marketing_source || '',
        });
        // Existing data is authoritative — don't let the auto-suggest effects touch it.
        setPriceTouched(true);
        setDatePromisedTouched(true);
      } catch (err) {
        console.error('Error loading appointment detail:', err);
        setError('Failed to load appointment details.');
      } finally {
        setLoading(false);
      }
    };
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  // Runs in edit mode too — if staff edits the customer name on an existing appointment
  // (e.g. fixing a typo), they need the dropdown to re-attach to the right existing
  // customer instead of silently creating a new, disconnected Customer record on save.
  useEffect(() => {
    const searchCustomers = async () => {
      if (customerName.length < 2) {
        setCustomerSearchResults([]);
        return;
      }
      try {
        const response = await fetchUsers(customerName);
        setCustomerSearchResults(response.results || []);
      } catch (err) {
        console.error('Error searching customers:', err);
      }
    };
    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [customerName]);

  const selectedType = appointmentTypes.find((t) => String(t.id) === String(formData.appointment_type));

  // Auto-suggest price/date-promised from the selected service until the user edits
  // either field directly (or this is an existing appointment with stored values).
  useEffect(() => {
    if (priceTouched || !selectedType) return;
    setFormData((prev) => ({ ...prev, price: computeSuggestedPrice(selectedType, prev.number_of_wheels) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.appointment_type, formData.number_of_wheels, priceTouched]);

  useEffect(() => {
    if (datePromisedTouched || !selectedType) return;
    setFormData((prev) => ({ ...prev, date_promised: computeSuggestedDatePromised(prev.date, selectedType) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.appointment_type, formData.date, datePromisedTouched]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setFormData((prev) => ({
      ...prev,
      customer_phone: customer.phone || prev.customer_phone,
      customer_email: customer.email || prev.customer_email,
    }));
    setShowDropdown(false);
  };

  const handleCustomerNameChange = (e) => {
    setCustomerName(e.target.value);
    setSelectedCustomer(null);
    setShowDropdown(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'price') setPriceTouched(true);
    if (name === 'date_promised') setDatePromisedTouched(true);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Resolves the typed/selected customer to a Customer id — mirrors the New Work Order
  // flow: an unselected name goes through the duplicate-customer check, where staff pick
  // an existing customer or create a new one. Never overwrites an existing customer's
  // saved phone/email. Returns null if staff went back to edit the form.
  const resolveCustomerId = async () => {
    if (selectedCustomer) return selectedCustomer.id;
    const customerData = {
      name: customerName,
      phone: formData.customer_phone,
      email: formData.customer_email,
    };
    try {
      const choice = await confirmNewCustomer(customerData);
      if (!choice) return null;
      const customer = choice.action === 'use' ? choice.customer : await createCustomer(customerData);
      // Remember it, so retrying after a failed save doesn't ask (or create) again.
      setSelectedCustomer(customer);
      return customer.id;
    } catch (err) {
      console.error('Error saving customer:', err);
      setError('Failed to save customer information.');
      return null;
    }
  };

  const submitAppointment = async (payload, override) => {
    const body = override ? { ...payload, override: true } : payload;
    setSaving(true);
    try {
      if (isEditMode) {
        await updateAppointment(appointment.id, body);
      } else {
        const created = await createAppointment(body);
        if (onCreated) onCreated(created);
      }
      onClose(true);
    } catch (err) {
      const code = err.response?.data?.code;
      if (err.response?.status === 409 && CONFLICT_CODES.includes(code)) {
        setBookingConflict({ payload, code, message: err.response.data.error });
      } else {
        console.error('Error saving appointment:', err);
        setError(err.response?.data?.error || err.response?.data?.non_field_errors?.[0] || 'Failed to save appointment.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setBookingConflict(null);

    if (!customerName.trim()) {
      setError('Customer name is required.');
      return;
    }
    if (!formData.appointment_type) {
      setError('Appointment type is required.');
      return;
    }
    if (!formData.date || !formData.time) {
      setError('Date and time are required.');
      return;
    }

    setSaving(true);
    const customerId = await resolveCustomerId();
    if (!customerId) {
      setSaving(false);
      return;
    }

    const startDatetime = new Date(`${formData.date}T${formData.time}:00`);
    const slotMinutes = (selectedType && selectedType.slot_interval_minutes) || slotIntervalMinutes || 30;
    const keepOriginalLength = original && original.durationMs > 0
      && original.appointmentType === String(formData.appointment_type);
    const durationMs = keepOriginalLength ? original.durationMs : slotMinutes * 60000;
    const endDatetime = new Date(startDatetime.getTime() + durationMs);

    const payload = {
      customer_name: customerName,
      customer_phone: formData.customer_phone,
      customer_email: formData.customer_email,
      customer: customerId,
      appointment_type: formData.appointment_type,
      vehicle: formData.vehicle,
      start_datetime: startDatetime.toISOString(),
      end_datetime: endDatetime.toISOString(),
      number_of_wheels: formData.number_of_wheels || 1,
      price: formData.price || null,
      date_promised: formData.date_promised || null,
      description: formData.description,
      staff_notes: formData.staff_notes,
      customer_notes: formData.customer_notes,
    };

    if (isEditMode) {
      payload.status = formData.status;
    }

    const promptKind = isEditMode ? emailPromptKind(startDatetime) : null;
    if (promptKind && formData.customer_email.trim() && !emailOptedOut) {
      setSaving(false);
      setEmailPrompt({
        kind: promptKind,
        payload,
        oldStart: new Date(original.startMs),
        newStart: startDatetime,
      });
      return;
    }

    // Edits never email unless staff said yes above; the backend treats a missing
    // send_email as "no" for edits. New bookings omit it and get their confirmation.
    await submitAppointment(isEditMode ? { ...payload, send_email: false } : payload, false);
  };

  // Which "email the customer?" question this edit needs, if any. Cancelling wins over
  // a time change — the customer only needs to hear that it's off.
  const emailPromptKind = (startDatetime) => {
    if (!original) return null;
    if (formData.status === 'cancelled') return original.status !== 'cancelled' ? 'cancel' : null;
    if (formData.status === 'arrived') return null;
    if (original.status === 'cancelled' && formData.status === 'scheduled') return 'revive';
    if (startDatetime.getTime() !== original.startMs) return 'time';
    return null;
  };

  const handleEmailPromptChoice = async (sendEmail) => {
    const prompt = emailPrompt;
    setEmailPrompt(null);
    if (prompt.fromCancelButton) {
      await cancelWithEmailChoice(sendEmail);
    } else {
      await submitAppointment({ ...prompt.payload, send_email: sendEmail }, false);
    }
  };

  const handleOverrideConfirm = () => {
    if (!bookingConflict) return;
    submitAppointment(bookingConflict.payload, true);
  };

  const handleCancelAppointment = async () => {
    if (formData.customer_email.trim() && !emailOptedOut) {
      setEmailPrompt({ kind: 'cancel', fromCancelButton: true });
      return;
    }
    if (!window.confirm('Cancel this appointment? It will stay on the calendar, shaded.')) return;
    await cancelWithEmailChoice(false);
  };

  const cancelWithEmailChoice = async (sendEmail) => {
    setSaving(true);
    try {
      await cancelAppointment(appointment.id, { sendEmail });
      onClose(true);
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setError('Failed to cancel appointment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAppointment = async () => {
    if (!window.confirm('Permanently delete this appointment? This cannot be undone.')) return;
    setSaving(true);
    try {
      await deleteAppointment(appointment.id);
      onClose(true);
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment.');
    } finally {
      setSaving(false);
    }
  };

  const buildWorkOrderInitialData = () => ({
    customer: selectedCustomer ? selectedCustomer.id : (appointment && appointment.customer) || null,
    customer_name: customerName,
    phone: formData.customer_phone,
    customer_email: formData.customer_email,
    vehicle: formData.vehicle,
    service_requested: selectedType ? selectedType.work_order_service : 'Other',
    number_of_wheels: formData.number_of_wheels,
    description: formData.description,
    notes: formData.staff_notes,
    price: formData.price,
    date_promised: formData.date_promised,
  });

  const handleWorkOrderCreated = async (newWorkOrder) => {
    setShowWorkOrderModal(false);
    try {
      await updateAppointment(appointment.id, { status: 'arrived', work_order: newWorkOrder.id, override: true });
      onClose(true);
    } catch (err) {
      console.error('Error marking appointment arrived:', err);
      setError('Work order created, but the appointment status failed to update — please refresh.');
    }
  };

  return (
    <div className="appointment-modal-overlay" onClick={() => !showWorkOrderModal && onClose(false)}>
      <div
        className="appointment-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={showWorkOrderModal ? { display: 'none' } : undefined}
      >
        <div className="appointment-modal-header">
          <h2>{isEditMode ? 'Appointment' : 'New Appointment'}</h2>
          <button type="button" className="close-button" onClick={() => onClose(false)}>×</button>
        </div>

        {loading ? (
          <div className="appointment-modal-loading">Loading…</div>
        ) : (
          <form onSubmit={handleSave} className="appointment-modal-form">
            {error && <div className="appointment-modal-error">{error}</div>}

            <div className="appointment-form-row">
              <div className="customer-search-container">
                <label className="appointment-label">
                  Customer:
                  <input
                    className="appointment-input"
                    type="text"
                    value={customerName}
                    onChange={handleCustomerNameChange}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    placeholder="Search or enter new customer"
                    required
                  />
                </label>
                {showDropdown && customerName.length >= 2 && (
                  <div className="customer-dropdown">
                    {customerSearchResults.length > 0 ? (
                      customerSearchResults.map((c) => (
                        <div key={c.id} className="customer-dropdown-item" onClick={() => handleCustomerSelect(c)}>
                          {customerLabel(c)}
                        </div>
                      ))
                    ) : (
                      <div className="customer-dropdown-item">No customers found</div>
                    )}
                  </div>
                )}
              </div>

              <label className="appointment-label">
                Phone:
                <input
                  className="appointment-input"
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleInputChange}
                />
              </label>
            </div>

            <div className="appointment-form-row">
              <label className="appointment-label">
                Email:
                <input
                  className="appointment-input"
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                />
              </label>
              <label className="appointment-label">
                Vehicle:
                <input
                  className="appointment-input"
                  type="text"
                  name="vehicle"
                  value={formData.vehicle}
                  onChange={handleInputChange}
                  placeholder="e.g. 2020 BMW 340i"
                />
              </label>
            </div>

            <div className="appointment-form-row">
              <label className="appointment-label">
                Service:
                <select
                  className="appointment-input"
                  name="appointment_type"
                  value={formData.appointment_type}
                  onChange={handleInputChange}
                  required
                >
                  {appointmentTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
              {isEditMode && (
                <label className="appointment-label">
                  Status:
                  <select
                    className="appointment-input"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="arrived">Arrived</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
              )}
            </div>

            <div className="appointment-form-row">
              <label className="appointment-label">
                Date:
                <input
                  className="appointment-input"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label className="appointment-label">
                Time:
                <input
                  className="appointment-input"
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                />
              </label>
            </div>

            <div className="appointment-form-row">
              <label className="appointment-label">
                Number of Wheels:
                <input
                  className="appointment-input"
                  type="number"
                  min="1"
                  name="number_of_wheels"
                  value={formData.number_of_wheels}
                  onChange={handleInputChange}
                />
              </label>
              <label className="appointment-label">
                Price:
                <input
                  className="appointment-input"
                  type="number"
                  step="0.01"
                  min="0"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                />
              </label>
            </div>

            <div className="appointment-form-row">
              <label className="appointment-label">
                Date Promised:
                <input
                  className="appointment-input"
                  type="date"
                  name="date_promised"
                  value={formData.date_promised}
                  onChange={handleInputChange}
                />
              </label>
            </div>

            {/* How the customer found the website, recorded when they booked online. */}
            {isEditMode && formData.source === 'website' && formData.marketing_channel && (
              <div className="appointment-form-row">
                <label className="appointment-label">
                  Found us via:
                  <input
                    className="appointment-input"
                    value={
                      formData.marketing_source && formData.marketing_source !== formData.marketing_channel
                        ? `${formData.marketing_channel} (${formData.marketing_source})`
                        : formData.marketing_channel
                    }
                    readOnly
                  />
                </label>
              </div>
            )}

            {isEditMode && formData.source === 'website' && formData.customer_notes && (
              <div className="appointment-form-row">
                <label className="appointment-label appointment-label-full">
                  Customer Notes (from booking):
                  <textarea
                    className="appointment-textarea"
                    value={formData.customer_notes}
                    readOnly
                  />
                </label>
              </div>
            )}

            <div className="appointment-form-row">
              <label className="appointment-label appointment-label-full">
                Description:
                <textarea
                  className="appointment-textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Vehicle/part condition, what needs to be done…"
                />
              </label>
            </div>

            <div className="appointment-form-row">
              <label className="appointment-label appointment-label-full">
                Staff Notes:
                <textarea
                  className="appointment-textarea"
                  name="staff_notes"
                  value={formData.staff_notes}
                  onChange={handleInputChange}
                />
              </label>
            </div>

            {/* Saved appointments only — there's no email history for one that doesn't
                exist yet, and nothing to resend. */}
            {isEditMode && (
              <AppointmentEmails
                appointmentId={appointment.id}
                appointmentStatus={formData.status}
                customerEmail={formData.customer_email}
                optedOut={emailOptedOut}
              />
            )}

            <div className="appointment-modal-actions">
              {isEditMode && (
                <button type="button" className="appointment-delete-btn" onClick={handleDeleteAppointment} disabled={saving}>
                  Delete
                </button>
              )}
              {isEditMode && formData.status !== 'cancelled' && (
                <button type="button" className="appointment-cancel-btn" onClick={handleCancelAppointment} disabled={saving}>
                  Cancel Appointment
                </button>
              )}
              {isEditMode && formData.status === 'scheduled' && (
                <button
                  type="button"
                  className="appointment-confirm-btn"
                  onClick={() => setShowWorkOrderModal(true)}
                  disabled={saving}
                >
                  Create Work Order
                </button>
              )}
              <button type="submit" className="appointment-save-btn" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}

        {emailPrompt && (
          <div className="appointment-override-overlay" onClick={() => setEmailPrompt(null)}>
            <div className="appointment-override-content appointment-email-prompt" onClick={(e) => e.stopPropagation()}>
              <h3>{EMAIL_PROMPTS[emailPrompt.kind].title}</h3>
              {emailPrompt.kind === 'time' && (
                <p>{formatWhen(emailPrompt.oldStart)} → {formatWhen(emailPrompt.newStart)}</p>
              )}
              {emailPrompt.kind === 'cancel' && <p>It will stay on the calendar, shaded.</p>}
              <p className="appointment-override-hint">
                {emailPrompt.kind === 'cancel'
                  ? `Email ${formData.customer_email} that it's cancelled?`
                  : `Send ${formData.customer_email} an email?`}
                {' '}Other changes are never emailed.
              </p>
              <div className="appointment-override-actions">
                <button type="button" className="appointment-cancel-btn" onClick={() => setEmailPrompt(null)} disabled={saving}>
                  Go back
                </button>
                <button type="button" className="appointment-cancel-btn" onClick={() => handleEmailPromptChoice(false)} disabled={saving}>
                  {EMAIL_PROMPTS[emailPrompt.kind].no}
                </button>
                <button type="button" className="appointment-confirm-btn" onClick={() => handleEmailPromptChoice(true)} disabled={saving}>
                  {EMAIL_PROMPTS[emailPrompt.kind].yes}
                </button>
              </div>
            </div>
          </div>
        )}

        {bookingConflict && (
          <div className="appointment-override-overlay" onClick={() => setBookingConflict(null)}>
            <div className="appointment-override-content" onClick={(e) => e.stopPropagation()}>
              <h3>{CONFLICT_TITLES[bookingConflict.code] || 'Booking conflict'}</h3>
              <p>{bookingConflict.message}</p>
              <p className="appointment-override-hint">Customers can&apos;t do this online — only staff can override here.</p>
              <div className="appointment-override-actions">
                <button type="button" className="appointment-cancel-btn" onClick={() => setBookingConflict(null)} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="appointment-confirm-btn" onClick={handleOverrideConfirm} disabled={saving}>
                  {saving ? 'Booking…' : 'Override & Book Anyway'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showWorkOrderModal && (
        <NewWorkOrderModal
          showModal={showWorkOrderModal}
          toggleModal={() => setShowWorkOrderModal(false)}
          onWorkOrderCreated={handleWorkOrderCreated}
          initialData={buildWorkOrderInitialData()}
        />
      )}
      {duplicatePrompt}
    </div>
  );
};

export default AppointmentModal;
